import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import ConfirmModal from "../components/ConfirmModal";
import { ordersAPI, testAPI, customersAPI } from "../services/api";
import { sanitizeName } from "../utils/helpers";

const Bestellungen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [backupOrder, setBackupOrder] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get("search") || "",
  );
  const [generatingData, setGeneratingData] = useState(false);
  const [testMessage, setTestMessage] = useState({ type: "", text: "" });
  const [showTestMenu, setShowTestMenu] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const customerDropdownRef = useRef(null);
  const [bonusCopied, setBonusCopied] = useState(false);
  const bonusCopyTimeoutRef = useRef(null);

  // URL-based params
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const statusFilter = searchParams.get("status") || "";
  const urlSearch = searchParams.get("search") || "";

  const [itemsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") || "").trim(),
  );

  // Helper to update URL params
  const updateParams = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === undefined || value === "") {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        });
        return newParams;
      });
    },
    [setSearchParams],
  );

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch off the URL search param (source of truth) rather than the
      // debounced state, which lags behind when returning from the detail
      // page and would otherwise trigger a stale unfiltered fetch.
      const response = await ordersAPI.getAll(
        currentPage,
        itemsPerPage,
        urlSearch.trim(),
        statusFilter,
      );
      if (response.data.success) {
        setOrders(response.data.data);
        setTotalOrders(response.data.total || 0);
        setTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, urlSearch, statusFilter]);

  const fetchCustomers = useCallback(
    async (page = 1, reset = false) => {
      if (loadingCustomers) return;

      try {
        setLoadingCustomers(true);
        const limit = 20;

        const response = await customersAPI.getAll(page, limit);
        if (response.data.success) {
          const newCustomers = response.data.data;
          const total = response.data.total || 0;
          const totalPages = response.data.pagination?.pages || 1;

          if (reset) {
            setCustomers(newCustomers);
          } else {
            setCustomers((prev) => [...prev, ...newCustomers]);
          }

          setTotalCustomers(total);
          setCustomerPage(page);
          setHasMoreCustomers(page < totalPages);
        }
      } catch (error) {
        console.error("Failed to fetch customers:", error);
      } finally {
        setLoadingCustomers(false);
      }
    },
    [loadingCustomers],
  );

  // Handle scroll in customer dropdown to load more
  const handleCustomerScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Load more when scrolled near bottom (within 50px)
      if (
        scrollHeight - scrollTop - clientHeight < 50 &&
        hasMoreCustomers &&
        !loadingCustomers
      ) {
        fetchCustomers(customerPage + 1);
      }
    },
    [customerPage, hasMoreCustomers, loadingCustomers, fetchCustomers],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          // Fetch single order
          setLoading(true);
          const response = await ordersAPI.getById(id);
          if (response.data.success) {
            setSelectedOrder(response.data.data);
          }
          setLoading(false);
        } else {
          // Start loading first page of customers in background
          fetchCustomers(1, true);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch orders when page, search, or filter changes
  useEffect(() => {
    if (!id) {
      fetchOrders();
    }
  }, [id, fetchOrders]);

  useEffect(() => {
    return () => {
      if (bonusCopyTimeoutRef.current) {
        clearTimeout(bonusCopyTimeoutRef.current);
      }
    };
  }, []);

  // Generate test orders for selected customer
  const handleGenerateOrdersForCustomer = async () => {
    if (!selectedCustomer) {
      setTestMessage({
        type: "error",
        text: "Bitte wählen Sie einen Kunden aus",
      });
      return;
    }
    setGeneratingData(true);
    setTestMessage({ type: "", text: "" });
    try {
      const response = await testAPI.generateOrders(selectedCustomer, 3);
      if (response.data.success) {
        setTestMessage({ type: "success", text: response.data.message });
        await fetchOrders();
        setSelectedCustomer("");
      }
    } catch (error) {
      setTestMessage({
        type: "error",
        text: error.message || "Fehler beim Erstellen der Testdaten",
      });
    } finally {
      setGeneratingData(false);
      setShowTestMenu(false);
    }
  };

  // Generate complete test data (customers + orders)
  const handleGenerateCompleteData = async () => {
    setGeneratingData(true);
    setTestMessage({ type: "", text: "" });
    try {
      const response = await testAPI.generateCompleteData(3, 4);
      if (response.data.success) {
        setTestMessage({ type: "success", text: response.data.message });
        await Promise.all([fetchOrders(), fetchCustomers(1, true)]);
      }
    } catch (error) {
      setTestMessage({
        type: "error",
        text: error.message || "Fehler beim Erstellen der Testdaten",
      });
    } finally {
      setGeneratingData(false);
      setShowTestMenu(false);
    }
  };

  // Clear all test data
  const handleClearTestData = async () => {
    setGeneratingData(true);
    setTestMessage({ type: "", text: "" });
    try {
      const response = await testAPI.clearTestData();
      if (response.data.success) {
        setTestMessage({ type: "success", text: response.data.message });
        await Promise.all([fetchOrders(), fetchCustomers(1, true)]);
      }
    } catch (error) {
      setTestMessage({
        type: "error",
        text: error.message || "Fehler beim Löschen der Testdaten",
      });
    } finally {
      setGeneratingData(false);
      setShowTestMenu(false);
    }
  };

  const formatCurrency = (value) => {
    return (value || 0).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    if (textarea.parentNode === document.body) {
      document.body.removeChild(textarea);
    }
  };

  const handleCopyBonusValue = async () => {
    try {
      const valueToCopy = formatCurrency(discountValue);
      await copyToClipboard(valueToCopy);
      setBonusCopied(true);
      if (bonusCopyTimeoutRef.current) {
        clearTimeout(bonusCopyTimeoutRef.current);
      }
      bonusCopyTimeoutRef.current = setTimeout(() => {
        setBonusCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy bonus value:", error);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("de-DE");
  };

  const getStatusInfo = (order) => {
    // Show discount group status if order is in a discount group
    if (order.discountStatus === "pending") {
      return {
        status: "Ausstehend",
        color: "text-orange-500 bg-white border-orange-300",
      };
    }
    if (order.discountStatus === "available") {
      return {
        status: "Offen",
        color: "text-green-500 bg-white border-green-300",
      };
    }
    if (order.discountStatus === "redeemed") {
      return {
        status: "Eingelöst",
        color: "bg-gray-400 text-white border-gray-400",
      };
    }
    // No discount group - show dash
    return { status: "-", color: "text-gray-400 bg-gray-50 border-gray-200" };
  };

  // Orders are now filtered and paginated by the API
  const paginatedOrders = orders;

  // Pagination calculations (using API data)
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalOrders);

  // Debounce search term — sync to URL (like Bonus.js)
  useEffect(() => {
    const timer = setTimeout(() => {
      const normalizedSearch = searchTerm.trim();
      setDebouncedSearch(normalizedSearch);

      const hasSearchChanged = normalizedSearch !== urlSearch.trim();
      if (hasSearchChanged) {
        updateParams({ search: normalizedSearch || null, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Keep input in sync with URL (back/forward navigation)
  useEffect(() => {
    setSearchTerm((prev) => (prev === urlSearch ? prev : urlSearch));
    setDebouncedSearch((prev) =>
      prev === urlSearch.trim() ? prev : urlSearch.trim(),
    );
  }, [urlSearch]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      updateParams({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handlePrevPage = () => {
    updateParams({ page: Math.max(currentPage - 1, 1) });
  };

  const handleNextPage = () => {
    updateParams({ page: Math.min(currentPage + 1, totalPages) });
  };

  // Open delete confirmation modal
  const handleOpenDeleteModal = (itemId) => {
    setDeleteModal({ isOpen: true, itemId });
  };

  // Close delete confirmation modal
  const handleCloseDeleteModal = () => {
    setDeleteModal({ isOpen: false, itemId: null });
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (deleteModal.itemId && selectedOrder) {
      try {
        await ordersAPI.deleteItem(selectedOrder._id, deleteModal.itemId);
        setSelectedOrder((prev) => ({
          ...prev,
          items: prev.items.filter(
            (item) => item.orderLineId !== deleteModal.itemId,
          ),
        }));
      } catch (error) {
        console.error("Failed to delete item:", error);
      }
    }
    handleCloseDeleteModal();
  };

  // Toggle bonus checkbox
  const handleToggleBonus = (itemId) => {
    setSelectedOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.orderLineId === itemId
          ? { ...item, discountEligible: !item.discountEligible }
          : item,
      ),
    }));
  };

  // Enter edit mode
  const handleEnterEditMode = () => {
    setBackupOrder(JSON.parse(JSON.stringify(selectedOrder)));
    setIsEditMode(true);
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      for (const item of selectedOrder.items) {
        await ordersAPI.updateItem(selectedOrder._id, item.orderLineId, {
          discountEligible: item.discountEligible,
        });
      }
      setBackupOrder(null);
      setIsEditMode(false);
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setSaving(false);
    }
  };

  // Cancel changes
  const handleCancel = () => {
    if (backupOrder) {
      setSelectedOrder(backupOrder);
    }
    setBackupOrder(null);
    setIsEditMode(false);
  };

  // Orders List View (when no ID)
  if (!id) {
    return (
      <Layout>
        {/* Test Message */}
        {testMessage.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              testMessage.type === "success"
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-red-50 border border-red-200 text-red-700"
            }`}
          >
            {testMessage.text}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Einkäufe</h1>
            <p className="text-gray-500 mt-1">
              {totalOrders} Einkäufe
              {(debouncedSearch || statusFilter) && " (gefiltert)"}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Einkaufsnr., Kundenname, Kundennr. suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full max-w-xs pl-4 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 text-sm"
              />
              {searchTerm ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  onClick={() => setSearchTerm("")}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              )}
            </div>

            {/* Status Filter */}
            {/* <select
              value={statusFilter}
              onChange={(e) =>
                updateParams({ status: e.target.value, page: 1 })
              }
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 text-sm bg-white"
            >
              <option value="">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="available">Offen</option>
              <option value="redeemed">Eingelöst</option>
            </select> */}

            {/* Test Data Button with Dropdown */}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <svg
                className="animate-spin h-8 w-8 text-gray-400"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : paginatedOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Keine Einkäufe gefunden
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                        Einkaufsnummer
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                        Kunde
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                        Datum
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                        Betrag
                      </th>
                      {/* <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                        Status
                      </th> */}
                      <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                        Aktion
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders?.map((order) => {
                      const isSheet = order.source === "sheet";
                      return (
                        <tr
                          key={order._id}
                          className="border-b border-gray-50 hover:bg-gray-50"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {order.posReference}
                              </span>
                              {isSheet && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                  Aus Tabelle
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {sanitizeName(order.customerId?.name)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.customerId?.contactId || "-"}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {isSheet ? "Aus Tabelle" : formatDate(order.orderDate)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            € {formatCurrency(order.amountTotal)}
                          </td>
                          <td className="px-6 py-4">
                            {isSheet ? (
                              <span className="text-sm text-gray-300">–</span>
                            ) : (
                              <button
                                onClick={() =>
                                  navigate(`/bestellungen/${order._id}`, {
                                    state: {
                                      orderNumber: order.posReference,
                                      customerName: order.customerId?.name,
                                    },
                                  })
                                }
                                className="text-sm px-4 py-3 rounded-lg bg-gray-800 text-white hover:bg-gray-900 font-medium tracking-wide transition-all duration-500 ease-in-out hover:-translate-y-[1px]"
                              >
                                Details
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls - Always show */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Zeige {totalOrders > 0 ? startIndex + 1 : 0}-{endIndex} von{" "}
                  {totalOrders} Einkäufe
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Zurück
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        if (totalPages <= 7) return true;
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <button
                            onClick={() => updateParams({ page })}
                            className={`px-3 py-1 text-sm font-medium rounded-lg ${
                              currentPage === page
                                ? "bg-gray-900 text-white"
                                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Weiter
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Layout>
    );
  }

  // Order Detail View (when ID is provided)
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <svg
            className="animate-spin h-10 w-10 text-gray-400"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </Layout>
    );
  }

  if (!selectedOrder) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Einkauf nicht gefunden</p>
          <button
            onClick={() => navigate("/bestellungen")}
            className="mt-4 px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Zurück zur Liste
          </button>
        </div>
      </Layout>
    );
  }

  // Calculate values from order data
  // Use orderLines (from WAWI sync) or items (legacy) - merge both sources
  const customer = selectedOrder.customerId || {};
  let orderItems =
    selectedOrder.orderLines?.length > 0
      ? selectedOrder.orderLines.map((line) => {
          return {
            orderLineId: line.orderLineId || line._id,
            productId: line.productId,
            productName: line.fullProductName || line.productName,
            priceUnit: line.priceUnit,
            priceSubtotalIncl:
              line.priceSubtotalIncl || line.priceUnit * line.quantity,
            quantity: line.quantity || 1,
            discount: line.discount || 0,
            discountEligible:
              line.priceSubtotalIncl > 0 &&
              line?.discount === 0 &&
              !["bonus kundenkarte", "sonderrabatt"].includes(
                (line.fullProductName || line.productName || "").toLowerCase(),
              ),
            totalPurchaseExceptBonusCard: ![
              "bonus kundenkarte",
              "sonderrabatt",
            ].includes(
              (line.fullProductName || line.productName || "").toLowerCase(),
            ),
            // discountEligible: line.discountEligible !== false,
            image: line.productRef?.image || null,
            color:
              line.productRef?.attributeValues?.find(
                (a) => a.attributeName === "Farbe",
              )?.valueName || null,
            material:
              line.productRef?.attributeValues?.find(
                (a) => a.attributeName === "Material",
              )?.valueName || null,
          };
        })
      : selectedOrder.items || [];

  // Previously filtered out return lines (discount===0 && priceSubtotalIncl<0
  // && !sonderrabatt/bonus kundenkarte) so they didn't show up in the detail.
  // Now we keep them visible AND let them net into the eligible amount below,
  // so a mixed receipt (purchase + return) reflects the true net total and a
  // pure-return receipt produces a negative bonus to deduct from the balance.
  // orderItems = orderItems?.filter((line) => {
  //   const name = (line.productName || "").toLowerCase();
  //   const isInvalid =
  //     line?.discount === 0 &&
  //     line.priceSubtotalIncl < 0 &&
  //     !["bonus kundenkarte", "sonderrabatt"].some((n) => name.includes(n));
  //   return !isInvalid; // ✅ always return boolean
  // });

  console.log("orderItems", orderItems);

  const discountEligibleItems = orderItems.filter(
    (item) => item.discountEligible,
  );

  // const discountEligibleAmount = discountEligibleItems.reduce(
  //   (sum, item) =>
  //     sum + (item.priceSubtotalIncl || item.priceUnit) * (item.quantity || 1),
  //   0,
  // );

  // Previous netting logic excluded plain returns (amount<0 and not a
  // Sonderrabatt/Bonus Kundenkarte name) — that's why mixed receipts
  // registered the gross purchase amount instead of the net.
  // const discountEligibleAmount = orderItems.reduce((sum, item) => {
  //   const name = (item.productName || "").toLowerCase();
  //   const amount =
  //     item.priceSubtotalIncl ?? item.priceUnit * (item.quantity || 1);
  //   if ((item.discount || 0) > 0) return sum;
  //   if (["sonderrabatt", "bonus kundenkarte"].some((n) => name.includes(n))) {
  //     return sum + amount;
  //   }
  //   if (amount < 0) return sum;
  //   return sum + amount;
  // }, 0);

  const discountEligibleAmount = orderItems.reduce((sum, item) => {
    const amount =
      item.priceSubtotalIncl ?? item.priceUnit * (item.quantity || 1);

    // exclude positive Sale items (existing per-line discount); a refunded
    // sale item (negative qty + negative amount) is still netted.
    if ((item.discount || 0) > 0 && amount > 0) return sum;

    // net everything else (purchases, returns, Sonderrabatt, Bonus Kundenkarte)
    return sum + amount;
  }, 0);

  const totalPurchaseExceptBonusCard = orderItems.filter(
    (item) => item?.totalPurchaseExceptBonusCard,
  );

  console.log(discountEligibleItems, { discountEligibleAmount });
  // Previous formula multiplied priceSubtotalIncl (which already includes the
  // quantity and its sign) by quantity again — double-negating returns
  // (-79.90 * -1 = +79.90) and inflating multi-qty lines. Use the signed line
  // subtotal directly so a pure-return receipt totals negative.
  // const totalPurchaseExceptBonusCardAmount =
  //   totalPurchaseExceptBonusCard.reduce(
  //     (sum, item) =>
  //       sum + (item.priceSubtotalIncl || item.priceUnit) * (item.quantity || 1),
  //     0,
  //   );
  const totalPurchaseExceptBonusCardAmount =
    totalPurchaseExceptBonusCard.reduce(
      (sum, item) =>
        sum + (item.priceSubtotalIncl ?? item.priceUnit * (item.quantity || 1)),
      0,
    );

  // console.log("discountEligibleAmount", discountEligibleAmount);

  // const totalPurchaseAmountExcudingSalesItems
  // Previously clamped to 0 for negative totals; now we allow negative bonus
  // so pure-return receipts visibly show the deduction amount in the tile.
  // const discountValue =
  //   discountEligibleAmount > 0 ? discountEligibleAmount * 0.1 : 0;
  const discountValue = discountEligibleAmount * 0.1;
  // selectedOrder?.amountTotal > 0 ? selectedOrder.amountTotal * 0.1 : 0;
  // console.log({
  //   discountEligibleItems,
  //   discountEligibleAmount,
  //   discountValue,
  //   a: selectedOrder.orderLines,
  //   selectedOrder,
  // });
  return (
    <Layout>
      {/* Header with Order Number and Back Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <span className="text-gray-600 font-medium">Einkaufsnummer:</span>
          <span className="bg-gray-100 px-4 py-2 rounded-lg font-semibold text-gray-900">
            {selectedOrder.posReference}
          </span>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-900 font-medium tracking-wide transition-all duration-500 ease-in-out hover:-translate-y-[1px]"
        >
          Zurück
        </button>
      </div>

      {/* Stats Section - Kundendetails on left, 2x2 grid on right */}
      <div className="flex gap-4 mb-6">
        {/* Customer Details - Left Column */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 w-[400px]">
          <div className="flex items-center justify-center gap-2 mb-6">
            <h3 className="text-center font-semibold text-gray-900">
              Kundendetails
            </h3>
            {(customer?._id || customer?.id) && (
              <button
                type="button"
                title="Bonusdetails öffnen"
                onClick={() =>
                  navigate(`/bonus/${customer._id || customer.id}`, {
                    state: { customerName: customer.name },
                  })
                }
                className="text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex">
              <span className="text-gray-500 w-36">Kundennummer:</span>
              <span className="text-gray-900">
                {customer?.contactId || `KUNDE-${customer?.ref}`}
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-500 w-36">Kundenname:</span>
              <span className="text-gray-900">
                {sanitizeName(customer.name)}
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-500 w-36">E-Mail:</span>
              <span className="text-gray-900">{customer.email || "-"}</span>
            </div>
            <div className="flex">
              <span className="text-gray-500 w-36">Telefonnummer:</span>
              <span className="text-gray-900">
                {customer.phone || customer.mobile || "-"}
              </span>
            </div>
            <div className="flex">
              <span className="text-gray-500 w-36">Adresse:</span>
              <div className="text-gray-900">
                <div>{customer.address?.street || "-"}</div>
                <div>
                  {customer.address?.postalCode} {customer.address?.city}
                </div>
                <div>{customer.address?.country}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - 2x2 Grid */}
        <div className="flex-1 grid grid-cols-2 gap-4">
          {/* Order Date */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center justify-center">
            <h3 className="font-semibold text-gray-900 mb-4">Einkaufsdatum</h3>
            <p className="text-3xl font-bold text-gray-900">
              {formatDate(selectedOrder.orderDate)}
            </p>
          </div>

          {/* Discount Value */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center justify-center">
            <div className="group flex flex-col items-center justify-center w-full focus:outline-none">
              <h3 className="font-semibold text-gray-900 mb-4">Bonuswert</h3>
              <p className="text-3xl font-bold text-gray-900 group-hover:text-gray-700">
                € {formatCurrency(discountValue)}
              </p>
            </div>
          </div>

          {/* Total Order Value */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center justify-center">
            <h3 className="font-semibold text-gray-900 mb-4">
              {/* Einkäufe gesamt */}
              {/* Rechnungssumme */}
              Bonusberechtigte Rechungssumme
            </h3>
            <p className="text-3xl font-bold text-gray-900">
              {/* total purchase except bonus card */}€{" "}
              {formatCurrency(discountEligibleAmount)}
              {/* {formatCurrency(selectedOrder.amountTotal)} */}
            </p>
          </div>

          {/* Total Without Sales Items */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center justify-center">
            <h3 className="font-semibold text-gray-900">Einkäufe gesamt</h3>

            <p className="text-3xl font-bold mt-3 text-gray-900">
              {/* € {formatCurrency(discountEligibleAmount)} */}€{" "}
              {formatCurrency(totalPurchaseExceptBonusCardAmount)}
            </p>

            <p className="text-xs text-gray-400 mt-1">
              {/* (ohne Sale-Artikel und Bonus) */}
              (ausgenommen Bonuskarte)
            </p>
          </div>
        </div>
      </div>

      {/* Purchase History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-gray-900 text-lg">Kaufhistorie</h3>
          {/* Hide edit button if order is redeemed */}
          {/* {selectedOrder.discountStatus !== "redeemed" &&
            (isEditMode ? (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                >
                  {saving ? "Speichern..." : "Speichern"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                >
                  Abbrechen
                </button>
              </div>
            ) : (
              <button
                onClick={handleEnterEditMode}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            ))} */}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {orderItems.map((item) => (
            <div
              key={item.orderLineId}
              className="flex gap-4 p-4 border border-gray-200 rounded-xl"
            >
              <img
                src={
                  item.image ||
                  "https://75ea8d4d59.wawi.onretail.eu/web/image/product.template/472/image_256"
                }
                alt={item.productName}
                className="w-24 h-24 object-cover rounded-lg"
                onError={(e) => {
                  e.target.src =
                    "https://75ea8d4d59.wawi.onretail.eu/web/image/product.template/472/image_256";
                }}
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {item.productName}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      {item.discount > 0 ? (
                        <>
                          <span className="text-red-500 font-semibold">
                            € {formatCurrency(item.priceSubtotalIncl)}
                          </span>
                          <span className="text-gray-400 line-through text-sm">
                            € {formatCurrency(item.priceUnit)}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-900">
                          € {formatCurrency(item.priceSubtotalIncl)}
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedOrder.discountStatus === "redeemed" ? (
                    // Show "Bonus angewendet" in gray for redeemed orders
                    item.discountEligible && (
                      <span className="text-gray-400 text-sm font-medium">
                        Bonus angewendet
                      </span>
                    )
                  ) : isEditMode ? (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.discountEligible}
                          onChange={() => handleToggleBonus(item.orderLineId)}
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <span className="text-sm text-gray-700">Bonus</span>
                      </label>
                      <button
                        onClick={() => handleOpenDeleteModal(item.orderLineId)}
                        className="p-1.5 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : item.discountEligible &&
                    Number(item.discount) === 0 &&
                    (item.priceSubtotalIncl || 0) > 0 ? (
                    <span className="text-green-600 text-sm font-medium">
                      Bonusberechtigt
                    </span>
                  ) : (
                    <span className="text-red-500 text-sm font-medium">
                      {Number(item.discount) === 0 &&
                      (item.priceSubtotalIncl || 0) < 0 &&
                      (item.quantity || 0) < 0 &&
                      !["bonus kundenkarte", "sonderrabatt"].some((n) =>
                        (item.productName || "").toLowerCase().includes(n),
                      )
                        ? "Nicht bonusberechtigt (Rückgabe)"
                        : "Nicht bonusberechtigt"}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Farbe: {item.color || "-"} | Material: {item.material || "-"}
                </p>
                <p className="text-sm text-gray-600">
                  Artikelnummer: {item.productId || "-"}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="ARTIKEL LÖSCHEN"
        message="Möchten Sie diesen Artikel wirklich löschen?"
        confirmText="Ja"
        cancelText="Stornieren"
      />
    </Layout>
  );
};

export default Bestellungen;
